import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd, preloadImage } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, CartUpdateEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const sectionId = this.refs.addToCartButton.dataset?.notifyFormSection;
    if (sectionId) {
      const root = this.closest('product-form-component') || document;
      const notifyForm =
        root.querySelector('#NotifyFormWrapper-' + sectionId + ' form.notify-when-available-form') ||
        root.querySelector('form.notify-when-available-form[data-notify-form-section="' + sectionId + '"]');
      if (notifyForm && typeof notifyForm.requestSubmit === 'function') {
        notifyForm.requestSubmit();
      }
      return;
    }

    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    const container = form?.closest('.product-details') || form?.closest('section') || form?.closest('[data-section-id]');
    const picker = container?.querySelector('variant-picker');
    const dropdowns = picker?.querySelectorAll('.custom-dropdown') ?? [];
    if (dropdowns.length > 0) {
      const allSelected = Array.from(dropdowns).every((d) => d.dataset.hasSelection === 'true');
      if (!allSelected) {
        event.preventDefault();
        const firstUnselected = Array.from(dropdowns).find((d) => d.dataset.hasSelection !== 'true');
        if (firstUnselected) {
          document.querySelectorAll('.custom-dropdown[data-open="true"]').forEach((d) => {
            d.dataset.open = 'false';
            d.querySelector('.custom-dropdown__trigger')?.setAttribute('aria-expanded', 'false');
          });
          firstUnselected.dataset.open = 'true';
          firstUnselected.querySelector('.custom-dropdown__trigger')?.setAttribute('aria-expanded', 'true');
          firstUnselected.querySelector('.custom-dropdown__trigger')?.focus();
        }
        return;
      }
    }

    // Check if adding would exceed max before animating
    const quantitySelector = /** @type {any} */ (form.querySelector('quantity-selector-component'));
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      // Don't animate if it would exceed max
      if (!validation.canAdd) {
        return;
      }
    }

    this.animateAddToCart();

    const animationEnabled = this.dataset.addToCartAnimation === 'true';

    if (animationEnabled && !event.target.closest('.quick-add-modal')) {
      this.#animateFlyToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart() {
    const { addToCartButton } = this.refs;

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    if (!addToCartButton.classList.contains('atc-added')) {
      addToCartButton.classList.add('atc-added');
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove('atc-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;


  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });

    const notifyForm = this.querySelector('form.notify-when-available-form');
    if (notifyForm) {
      notifyForm.addEventListener('submit', this.#onNotifyFormSubmit, { signal });
    }
  }

  /**
   * When variant changes: hide success/error message and show the notify form again.
   */
  #clearNotifySuccessState() {
    const wrapper =
      this.querySelector('.notify-when-available') ||
      document.querySelector('.notify-when-available');
    if (!wrapper) return;
    const successEl = wrapper.querySelector('.notify-when-available__success');
    const fieldsEl = wrapper.querySelector('.notify-when-available__fields');
    const errorEl = wrapper.querySelector('.notify-when-available__error');
    errorEl?.classList.add('hidden');
    if (!successEl || !fieldsEl) return;
    successEl.classList.add('hidden');
    fieldsEl.classList.remove('hidden');
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Fetches cart and updates quantity selector for current variant
   * @returns {Promise<number>} The cart quantity for the current variant
   */
  async #fetchAndUpdateCartQuantity() {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value) return 0;

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      const cartItem = cart.items.find(
        /** @param {any} item */
        (item) => item.variant_id.toString() === variantIdInput.value.toString()
      );
      const cartQty = cartItem ? cartItem.quantity : 0;

      // Use public API to update quantity selector
      const quantitySelector = /** @type {any} */ (this.querySelector('quantity-selector-component'));
      if (quantitySelector?.setCartQuantity) {
        quantitySelector.setCartQuantity(cartQty);
      }

      // Update quantity label if it exists
      this.#updateQuantityLabel(cartQty);

      return cartQty;
    } catch (error) {
      console.error('Failed to fetch cart quantity:', error);
      return 0;
    }
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    // Skip if this event came from this component
    if (event.detail?.sourceId === this.id || event.detail?.data?.source === 'product-form-component') return;

    await this.#fetchAndUpdateCartQuantity();
  };

  /**
   * Submits the notify-when-available form to the Klaviyo Client Back-in-Stock API via AJAX.
   * @param {Event} event
   */
  #onNotifyFormSubmit = async (event) => {
    const form = /** @type {HTMLFormElement} */ (event.target);
    if (!form?.classList?.contains('notify-when-available-form')) return;
    event.preventDefault();

    const wrapper = form.closest('.notify-when-available');
    const errorEl = wrapper?.querySelector('[data-notify-error]');
    const fieldsEl = wrapper?.querySelector('[data-notify-fields]');
    const successEl = /** @type {HTMLElement | null} */ (wrapper?.querySelector('[data-notify-success-visible]') ?? null);
    const submitButton = /** @type {HTMLButtonElement | null} */ (form.querySelector('.notify-when-available__submit'));
    const errorMessage = form.dataset.errorMessage || 'Something went wrong. Please try again.';

    const showError = () => {
      if (!errorEl) return;
      errorEl.textContent = errorMessage;
      errorEl.classList.remove('hidden');
    };

    errorEl?.classList.add('hidden');

    const publicKey = form.dataset.klaviyoPublicKey;
    const lang = (form.dataset.lang || '').toLowerCase();
    const emailInput = /** @type {HTMLInputElement | null} */ (form.querySelector('input[name="contact[email]"]'));
    const variantInput = /** @type {HTMLInputElement | null} */ (form.querySelector('input[name="contact[variant_id]"]'));
    const email = emailInput?.value.trim();
    const variantId = this.refs.variantId?.value || variantInput?.value || '';

    if (!publicKey || !email || !variantId) {
      showError();
      return;
    }

    if (submitButton) submitButton.disabled = true;

    const headers = {
      'Content-Type': 'application/json',
      accept: 'application/json',
      revision: '2026-04-15',
    };
    const companyId = encodeURIComponent(publicKey);

    try {
      if (lang) {
        try {
          await fetch(`https://a.klaviyo.com/client/profiles/?company_id=${companyId}`, {
            method: 'POST',
            headers,
            body: JSON.stringify({
              data: { type: 'profile', attributes: { email, properties: { language: lang } } },
            }),
          });
        } catch (error) {
          console.error('Klaviyo profile language update failed:', error);
        }
      }

      const response = await fetch(
        `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${companyId}`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({
            data: {
              type: 'back-in-stock-subscription',
              attributes: {
                channels: ['EMAIL'],
                profile: { data: { type: 'profile', attributes: { email } } },
              },
              relationships: {
                variant: { data: { type: 'catalog-variant', id: `$shopify:::$default:::${variantId}` } },
              },
            },
          }),
        }
      );

      if (!response.ok) {
        showError();
        return;
      }

      fieldsEl?.classList.add('hidden');
      if (successEl) {
        successEl.classList.remove('hidden');
        successEl.setAttribute('tabindex', '-1');
        successEl.focus();
      }
    } catch (error) {
      console.error('Klaviyo back-in-stock subscription failed:', error);
      showError();
    } finally {
      if (submitButton) submitButton.disabled = false;
    }
  };

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  handleSubmit(event) {
    const submittedForm = /** @type {HTMLFormElement | null} */ (
      /** @type {Event & { originalTarget?: EventTarget } } */ (event).originalTarget ?? event.target
    );

    if (
      submittedForm &&
      submittedForm instanceof HTMLFormElement &&
      submittedForm.classList.contains('notify-when-available-form')
    ) {
      return;
    }

    event.preventDefault();
    const form = /** @type {HTMLFormElement} */ (
      submittedForm?.getAttribute?.('data-type') === 'add-to-cart-form'
        ? submittedForm
        : this.querySelector('form[data-type="add-to-cart-form"]')
    );
    if (!form) return;

    const { addToCartTextError, addToCartButtonContainer } = this.refs;

    if (this.#timeout) clearTimeout(this.#timeout);

    // Check if the add to cart button is disabled and do an early return if it is
    if (addToCartButtonContainer?.refs.addToCartButton?.disabled) return;

    if (!form) throw new Error('Product form element missing');

    const quantitySelector = /** @type {any} */ (this.querySelector('quantity-selector-component'));
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        addToCartButtonContainer?.disable();

        const errorTemplate = this.dataset.quantityErrorMax || '';
        const errorMessage = errorTemplate.replace('{{ maximum }}', validation.maxQuantity.toString());
        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');

          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = errorMessage;
          } else {
            const newTextNode = document.createTextNode(errorMessage);
            addToCartTextError.appendChild(newTextNode);
          }

          this.#setLiveRegionText(errorMessage);

          if (this.#timeout) clearTimeout(this.#timeout);
          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        setTimeout(() => {
          addToCartButtonContainer?.enable();
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    const productId = (this.dataset.productId || '').toString();
    const engravingTextInput = form.querySelector('[data-engraving-text-input]');
    const storedText = (productId && typeof sessionStorage !== 'undefined')
      ? (sessionStorage.getItem('engraving_text_' + productId) || '').trim()
      : '';
    if (engravingTextInput && storedText && (!engravingTextInput.value || !engravingTextInput.value.trim())) {
      engravingTextInput.value = storedText;
    }

    const formData = new FormData(form);

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    });

    const mainVariantId = formData.get('id');
    const quantity = Number(formData.get('quantity')) || Number(this.dataset.quantityDefault) || 1;
    const engravingTextFromForm = (formData.get('engraving_text') || '').toString().trim();
    const engravingTextFromComponent = (this.dataset.engravingText || '').trim();
    const engravingTextFromStorage = (productId && typeof sessionStorage !== 'undefined')
      ? (sessionStorage.getItem('engraving_text_' + productId) || '').trim()
      : '';
    const engravingLabelEl = document.querySelector('[data-engraving-label]');
    const initialLabel = (engravingLabelEl?.getAttribute?.('data-initial-label') || '').trim();
    const triggerLabel = (engravingLabelEl?.textContent || '').trim();
    const engravingTextFromTrigger = (triggerLabel && triggerLabel !== initialLabel) ? triggerLabel : '';
    const popupInputEl = document.querySelector('[data-engraving-input]');
    const engravingTextFromPopup = (popupInputEl && popupInputEl.value) ? popupInputEl.value.trim() : '';
    const engravingTextFinal = engravingTextFromForm || engravingTextFromComponent || engravingTextFromStorage || engravingTextFromTrigger || engravingTextFromPopup;
    const engravingVariantId = formData.get('engraving_variant_id');
    const sectionsParam = cartItemComponentsSectionIds.length ? cartItemComponentsSectionIds.join(',') : '';

    const useNestedEngraving = mainVariantId && engravingVariantId && (engravingTextFinal.length > 0);

    let fetchPromise;
    if (useNestedEngraving) {
      const items = [
        { id: Number(mainVariantId), quantity },
        {
          id: Number(engravingVariantId),
          quantity: 1,
          parent_id: Number(mainVariantId),
          properties: { Engraving: engravingTextFinal },
        },
      ];
      const body = { items, ...(sectionsParam ? { sections: sectionsParam } : {}) };
      fetchPromise = fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(body),
      });
    } else {
      const fetchCfg = fetchConfig('javascript', { body: formData });
      fetchPromise = fetch(Theme.routes.cart_add_url, {
        ...fetchCfg,
        headers: {
          ...fetchCfg.headers,
          Accept: 'text/html',
        },
      });
    }

    fetchPromise
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
          );

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: quantity,
              productId: this.dataset.productId,
            })
          );

          return;
        }

        const id = formData.get('id');

        if (addToCartTextError) {
          addToCartTextError.classList.add('hidden');
          addToCartTextError.removeAttribute('aria-live');
        }

        if (!id) throw new Error('Form ID is required');

        if (this.dataset.engravingText) delete this.dataset.engravingText;
        if (productId) try { sessionStorage.removeItem('engraving_text_' + productId); } catch (e) {
          console.error('Failed to remove engraving text from session storage:', e);
        }
        const engravingTextInput = form.querySelector('[data-engraving-text-input]');
        if (engravingTextInput) engravingTextInput.value = '';

        if (this.refs.addToCartButtonContainer?.refs.addToCartButton) {
          const addToCartButton = this.refs.addToCartButtonContainer.refs.addToCartButton;
          const addedTextElement = addToCartButton.querySelector('.add-to-cart-text--added');
          const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;
          this.#setLiveRegionText(addedText);
          setTimeout(() => {
            this.#clearLiveRegionText();
          }, SUCCESS_MESSAGE_DISPLAY_DURATION);
        }

        this.#fetchAndUpdateCartQuantity();

        this.dispatchEvent(
          new CartAddEvent({}, id.toString(), {
            source: 'product-form-component',
            itemCount: quantity,
            productId: this.dataset.productId,
            sections: response.sections,
          })
        );
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        cartPerformance.measureFromEvent('add:user-action', event);
      });
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement('beforebegin', /** @type {Element} */(newElement.cloneNode(true)));
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = async (event) => {
    this.#clearNotifySuccessState();

    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId, addToCartButtonContainer } = this.refs;

    const currentAddToCartButton = addToCartButtonContainer?.refs.addToCartButton;
    const newAddToCartButton = event.detail.data.html.querySelector('[ref="addToCartButton"]');

    // Update the variant ID
    variantId.value = event.detail.resource?.id ?? '';

    if (!currentAddToCartButton && !this.refs.acceleratedCheckoutButtonContainer) return;

    const sectionId = this.dataset.sectionId;
    const notifyWrapper = sectionId ? this.querySelector('#NotifyFormWrapper-' + sectionId) : null;
    const notifyForm = notifyWrapper?.querySelector('form.notify-when-available-form');

    // Read availability from server-rendered HTML (Liquid already did the math)
    const newProductForm = event.detail.data.html.querySelector('product-form-component');
    const newAvailability = newProductForm?.dataset.availability || 'in-stock';
    const isMadeToOrder = newAvailability === 'made-to-order';
    this.dataset.availability = newAvailability;
    const productInfoRoot = this.closest('.product-information');
    if (productInfoRoot) productInfoRoot.dataset.availability = newAvailability;
    const sectionRoot = this.closest('.shopify-section');
    if (sectionRoot) sectionRoot.dataset.availability = newAvailability;

    // Update the button state and notify form visibility
    if (currentAddToCartButton) {
      if (notifyWrapper && notifyForm) {
        const resource = event.detail.resource;
        const unavailable = resource == null || resource.available === false;
        if (unavailable) {
          notifyWrapper.style.display = '';
          notifyWrapper.dataset.variantTitle = resource?.title ?? '';
          const vid = notifyForm.querySelector('input[name="contact[variant_id]"]');
          const vtitle = notifyForm.querySelector('input[name="contact[variant_title]"]');
          if (vid) vid.value = resource?.id ?? '';
          if (vtitle) vtitle.value = resource?.title ?? '';
          currentAddToCartButton.type = 'button';
          currentAddToCartButton.dataset.notifyFormSection = sectionId;
          currentAddToCartButton.classList.add('add-to-cart-button--notify');
          currentAddToCartButton.disabled = false;
          const textEl = currentAddToCartButton.querySelector('.add-to-cart-text__content');
          if (textEl) textEl.textContent = this.dataset.notifyButtonText || 'Mail me when available';
          addToCartButtonContainer.enable();
        } else {
          notifyWrapper.style.display = 'none';
          currentAddToCartButton.type = 'submit';
          delete currentAddToCartButton.dataset.notifyFormSection;
          currentAddToCartButton.classList.remove('add-to-cart-button--notify');
          currentAddToCartButton.disabled = false;
          const textEl = currentAddToCartButton.querySelector('.add-to-cart-text__content');
          if (textEl) {
            textEl.textContent = isMadeToOrder
              ? (this.dataset.madeToOrderButtonText || 'Add To Cart | Made To Order')
              : (this.dataset.addToCartButtonText || 'Add To Cart | In Stock');
          }
          addToCartButtonContainer.enable();
        }
      } else {
        if (event.detail.resource == null || event.detail.resource.available == false) {
          addToCartButtonContainer.disable();
        } else {
          addToCartButtonContainer.enable();
        }
        if (newAddToCartButton) {
          morph(currentAddToCartButton, newAddToCartButton);
        }
      }
    }

    if (this.refs.acceleratedCheckoutButtonContainer) {
      if (event.detail.resource == null || event.detail.resource.available == false) {
        this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      } else {
        this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
      }
    }

    // Set the data attribute for the add to cart button to the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      productVariantMedia &&
        addToCartButtonContainer?.setAttribute('data-product-variant-media', productVariantMedia + '&width=100');
    }

    // Update quantity selector's min/max/step attributes and cart quantity for the new variant
    const quantitySelector = /** @type {any} */ (this.querySelector('quantity-selector-component'));
    const newQuantityInput = /** @type {HTMLInputElement | null} */ (
      event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
    );

    if (quantitySelector?.updateConstraints && newQuantityInput) {
      quantitySelector.updateConstraints(
        newQuantityInput.min,
        newQuantityInput.max || null,
        newQuantityInput.step
      );
    }

    // Check if quantity rules are appearing/disappearing (causes layout shift)
    const quantityRules = this.refs.quantityRules;
    const newQuantityRules = event.detail.data.html.querySelector('.quantity-rules');
    const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

    if (isQuantityRulesChanging && quantitySelector) {
      // Store quantity value before morphing entire container
      const currentQuantityValue = quantitySelector.getValue?.();

      const currentProductFormButtons = this.refs.productFormButtons;
      const newProductFormButtons = event.detail.data.html.querySelector('.product-form-buttons');

      if (currentProductFormButtons && newProductFormButtons) {
        morph(currentProductFormButtons, newProductFormButtons);

        // Get the NEW quantity selector after morphing and update its constraints
        const newQuantitySelector = /** @type {any} */ (this.querySelector('quantity-selector-component'));
        const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
          event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
        );

        if (newQuantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
          // Temporarily set the old value so updateConstraints can snap it properly
          newQuantitySelector.setValue(currentQuantityValue);
          // updateConstraints will snap to valid increment if needed
          newQuantitySelector.updateConstraints(
            newQuantityInputElement.min,
            newQuantityInputElement.max || null,
            newQuantityInputElement.step
          );
        }
      }
    } else {
      // Update elements individually when layout isn't changing
      const quantityLabel = this.querySelector('.quantity-label');
      const newQuantityLabel = event.detail.data.html.querySelector('.quantity-label');
      this.#morphOrUpdateElement(quantityLabel, newQuantityLabel, quantitySelector);

      const addToCartButton = this.querySelector('[ref="addToCartButtonContainer"]');
      this.#morphOrUpdateElement(quantityRules, newQuantityRules, notifyWrapper ? null : addToCartButton);
    }

    // Re-apply notify form visibility and button state after any morph (so they stay correct)
    if (notifyWrapper && notifyForm) {
      const btn = this.querySelector('[ref="addToCartButton"]');
      const resource = event.detail.resource;
      const unavailable = resource == null || resource.available === false;
      if (btn) {
        if (unavailable) {
          notifyWrapper.style.display = '';
          notifyWrapper.dataset.variantTitle = resource?.title ?? '';
          const formInWrapper = notifyWrapper.querySelector('form.notify-when-available-form');
          if (formInWrapper) {
            const vid = formInWrapper.querySelector('input[name="contact[variant_id]"]');
            const vtitle = formInWrapper.querySelector('input[name="contact[variant_title]"]');
            if (vid) vid.value = resource?.id ?? '';
            if (vtitle) vtitle.value = resource?.title ?? '';
          }
          btn.type = 'button';
          btn.dataset.notifyFormSection = sectionId;
          btn.classList.add('add-to-cart-button--notify');
          btn.disabled = false;
          const textEl = btn.querySelector('.add-to-cart-text__content');
          if (textEl) textEl.textContent = this.dataset.notifyButtonText || 'Mail me when available';
        } else {
          notifyWrapper.style.display = 'none';
          btn.type = 'submit';
          delete btn.dataset.notifyFormSection;
          btn.classList.remove('add-to-cart-button--notify');
          btn.disabled = false;
          const textEl = btn.querySelector('.add-to-cart-text__content');
          if (textEl) {
            textEl.textContent = isMadeToOrder
              ? (this.dataset.madeToOrderButtonText || 'Add To Cart | Made To Order')
              : (this.dataset.addToCartButtonText || 'Add To Cart | In Stock');
          }
        }
      }
    }

    // Fetch and update cart quantity for the new variant
    await this.#fetchAndUpdateCartQuantity();
  };

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}

class FlyToCart extends HTMLElement {
  /** @type {Element} */
  source;

  /** @type {Element} */
  destination;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    const rect = this.getBoundingClientRect();
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    //Define bezier curve points
    // Maybe add half of the size of the flying thingy to the x and y to make it center properly
    const offset = {
      x: rect.width / 2,
      y: rect.height / 2,
    };
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offset.x,
      y: destinationRect.top + destinationRect.height / 2 - offset.y,
    };

    //Calculate the control points
    const controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 }; // Go up 200px
    const controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 }; // Go left 300px and up 100px

    //Animation variables
    /** @type {number | null} */
    let startTime = null;
    const duration = 600; // 600ms

    this.style.opacity = '1';

    /**
     * Animates the flying thingy along the bezier curve.
     * @param {number} currentTime - The current time.
     */
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Calculate current position along the bezier curve
      const position = bezierPoint(progress, startPoint, controlPoint1, controlPoint2, endPoint);

      //Update the position of the flying thingy
      this.style.setProperty('--x', `${position.x}px`);
      this.style.setProperty('--y', `${position.y}px`);

      // Scale down as it approaches the cart
      const scale = 1 - progress * 0.5;
      this.style.setProperty('--scale', `${scale}`);

      //Continue the animation if not finished
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        //Fade out the flying thingy
        this.style.opacity = '0';
        onAnimationEnd(this, () => this.remove());
      }
    };

    // Position the flying thingy back to the start point
    this.style.setProperty('--x', `${startPoint.x}px`);
    this.style.setProperty('--y', `${startPoint.y}px`);

    //Start the animation
    requestAnimationFrame(animate);
  }
}

/**
 * Calculates a point on a cubic Bézier curve.
 * @param {number} t - The parameter value (0 <= t <= 1).
 * @param {{x: number, y: number}} p0 - The starting point (x, y).
 * @param {{x: number, y: number}} p1 - The first control point (x, y).
 * @param {{x: number, y: number}} p2 - The second control point (x, y).
 * @param {{x: number, y: number}} p3 - The ending point (x, y).
 * @returns {{x: number, y: number}} The point on the curve.
 */
function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x);
  const bX = 3 * (p2.x - p1.x) - cX;
  const aX = p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);
  const bY = 3 * (p2.y - p1.y) - cY;
  const aY = p3.y - p0.y - cY - bY;

  const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x;
  const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y;

  return { x, y };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}
